import {Flex, Link, useTheme} from '@chakra-ui/react';
import React, {useEffect} from 'react';
import Icon from '../../../components/Icon';
import {TextG12} from '../../../components/typography';
import useBookmark from '../../../hooks/useBookmark';
import {getDirectionsLinkFromPlace} from '../../../utils/address';
import {Place} from '../../../types/article';
import {useNavigate, useSearchParams} from 'react-router-dom';
import ExternalLink from '../../../components/ExternalLink';
import useAuth from '../../../hooks/useAuth';
import {ToastMessage} from '../../../components/toast';
import {logShareEvent} from '../../../utils/analytics';
import Modal from "../../../components/Modals/Modal";
import EditForm from "../Edit/EditForm";
import {useFetchPotentialPlaceBusinessManagers} from "../../../hooks/useUsers";
import PotentialBusinessManagersModal from "../../../components/UsersList/PotentialBusinessManagersModal";
import {
  EntityRole,
  useGetCurrentUserRoleForEntity,
  useGetUsersAndRolesForEntity
} from "../../../hooks/useEntityUserRole";
import {assignUserRoleToEntity, removeUserRoleFromEntity} from "../../../endpoints/api";

const IconsHeader = ({
  place,
  preview = false,
  setSignUpRequiredOpen,
  refetchPlace,
}: {
  place: Place;
  preview?: boolean;
  setSignUpRequiredOpen?: any;
  refetchPlace: () => void;
}) => {
  const theme = useTheme();
  const {isBookmarked, handleOnBookmarkClick} = useBookmark(place, 'place');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {user} = useAuth();

  const [showEditOther, setShowEditOther] = React.useState(false);
  const [showBusinessManagersModal, setShowBusinessManagersModal] = React.useState(false);

  const { data: currentRole } = useGetCurrentUserRoleForEntity('places', place.id!);
  const canEditPlace = currentRole?.data === EntityRole.MANAGER || currentRole?.data === EntityRole.TEAM_MEMBER;

  useEffect(() => {
    if (!!searchParams.get('bookmark') && !isBookmarked) {
      handleOnBookmarkClick(null);
    }
  }, [searchParams]);

  const iconStyle: React.CSSProperties = React.useMemo(
    () => ({
      color: theme.colors.lightBlue,
      fontSize: '1.5rem',
    }),
    [theme]
  );

  const handleInviteClicked = async () => {
    const placeLink = location.origin + `/place/${place.uuid}/preview?invite-code=${user?.referralCode}`;
    const shareData = {
      title: place.title,
      text: `Hey! I found this place on Walkabout called '${place.title}' that I want to share with you! Check it out - `,
      url: placeLink,
    };

    try {
      if (navigator.share) {
        await navigator.share(shareData);
        logShareEvent('place', 'native_share', place.id, {
          item_name: place.title,
        });
      } else {
        // Fallback to clipboard copy if native share is not available
        await navigator.clipboard.writeText(placeLink);
        ToastMessage({
          status: 'success',
          text: 'Link copied to your clipboard',
        });
        logShareEvent('place', 'copy_link', place.id, {
          item_name: place.title,
        });
      }
    } catch (error) {
      // Handle share cancellation or error - fall back to clipboard
      try {
        await navigator.clipboard.writeText(placeLink);
        ToastMessage({
          status: 'success',
          text: 'Link copied to your clipboard',
        });
        logShareEvent('place', 'copy_link', place.id, {
          item_name: place.title,
        });
      } catch (clipboardError) {
        ToastMessage({
          status: 'error',
          text: 'Unable to share. Please copy the URL manually.',
        });
      }
    }
  };

  return (
    <Flex justify="center" gap=".5rem" whiteSpace="nowrap" flexWrap="wrap">
      {place.phone && (
        <Link href={`tel: +${place.phone}`} style={{textDecoration: 'none'}}>
          <Flex minW="60px" direction="column" align="center">
            <Icon iconName="fi-rr-phone-call" style={iconStyle} />
            <TextG12 color={theme.colors.lightBlue}>Call</TextG12>
          </Flex>
        </Link>
      )}
      <ExternalLink
        href={getDirectionsLinkFromPlace(place)}
        isExternal
        style={{textDecoration: 'none'}}>
        <Flex minW="60px" direction="column" align="center">
          <Icon iconName="fi-rr-map-marker" style={iconStyle} />
          <TextG12 color={theme.colors.lightBlue}>Directions</TextG12>
        </Flex>
      </ExternalLink>
      <Flex minW="60px" direction="column" align="center" onClick={handleInviteClicked}>
        <Icon iconName="fi-rr-user-add" style={iconStyle} />
        <TextG12 color={theme.colors.lightBlue}>Share</TextG12>
      </Flex>
      <Flex
        minW="60px"
        direction="column"
        align="center"
        onClick={
          preview
            ? () => {
                setSignUpRequiredOpen(true);
              }
            : handleOnBookmarkClick
        }>
        {isBookmarked ? (
          <Icon
            iconName="fi-sr-bookmark"
            style={preview ? {...iconStyle, color: theme.colors.grey} : iconStyle}
          />
        ) : (
          <Icon
            iconName="fi-rr-bookmark"
            style={preview ? {...iconStyle, color: theme.colors.grey} : iconStyle}
          />
        )}
        <TextG12 color={preview ? theme.colors.grey : theme.colors.lightBlue}>Bookmark</TextG12>
      </Flex>
      <Flex
        minW="60px"
        color={theme.colors.lightBlue}
        direction="column"
        align="center"
        onClick={() => navigate(`/place/${place.id}/chat`)}>
        <Icon iconName="fi-rr-comment" style={{fontSize: '1.5rem'}} />
        <TextG12>Place Chat</TextG12>
      </Flex>
      {place.website && (
        <ExternalLink href={place.website} isExternal style={{textDecoration: 'none'}}>
          <Flex minW="60px" direction="column" align="center">
            <Icon iconName="fi-rr-globe" style={iconStyle} />
            <TextG12 color={theme.colors.lightBlue}>Website</TextG12>
          </Flex>
        </ExternalLink>
      )}
      {(currentRole?.data === EntityRole.MANAGER) && (
        <>
          <Flex
            minW="60px"
            color={theme.colors.lightBlue}
            direction="column"
            align="center"
            onClick={() => setShowBusinessManagersModal(true)}>
            <Icon iconName="fi-rr-user-pen" style={{fontSize: '1.5rem'}} />
            <TextG12>Team</TextG12>
          </Flex>
          <PotentialBusinessManagersModal
            isOpen={showBusinessManagersModal}
            onClose={() => setShowBusinessManagersModal(false)}
            title="Users Management"
            assignUserRoleToEntity={(entityId: number, userId: number, roleId: number) => assignUserRoleToEntity('places', entityId, userId, roleId)}
            removeUserRoleFromEntity={(entityId: number, userId: number) => removeUserRoleFromEntity('places', entityId, userId)}
            fetchUsersAndRoles={(entityId: number) => useGetUsersAndRolesForEntity('places', entityId)}
            fetchUsersHook={useFetchPotentialPlaceBusinessManagers}
            entityId={place.id}
          />
        </>
      )}
      {canEditPlace && (
        <Flex
          minW="60px"
          color={theme.colors.lightBlue}
          direction="column"
          align="center"
          onClick={() => setShowEditOther(true)}>
            <Icon iconName="fi-rr-edit" style={{fontSize: '1.5rem'}} />
          <TextG12>Edit Other</TextG12>
        </Flex>
      )}
      <Modal
        show={showEditOther}
        parentCallback={() => setShowEditOther(false)}
        modalPadding={0}
        closeButton>
        <EditForm place={place} refetchPlace={refetchPlace} onClose={() => setShowEditOther(false)} />
      </Modal>
    </Flex>
  );
};

export default IconsHeader;
